import './MainLogo.scss';

import { environmentConfigSelector, storeIdentifierNameSelector } from 'store/selectors';

import React from 'react';
import { checkStores } from 'utility/utility';
import { useAppSelector } from 'store/store';

interface MainLogoImageDefaultProps {
  storeIdentifierName: string;
  environment: string;
}

const MainLogoImageDefault = ({ storeIdentifierName, environment }: MainLogoImageDefaultProps) => {
  return (
    <img
      src={`https://images.dickssportinggoods.com/assets/logo/${
        environment === 'prod' ? 'prod' : 'stage'
      }/${storeIdentifierName}/logo.svg`}
      alt="logo"
      data-testid="MainLogo-desktop-logo"
      fetchPriority="high"
    />
  );
};

interface MainLogoImagePLProps {
  environment: string;
  variant: 'default' | 'dark';
  sizes: 'responsive' | 'small' | 'large';
}

const MainLogoImagePL = ({ environment, variant, sizes }: MainLogoImagePLProps) => {
  const baseUrl = `https://images.dickssportinggoods.com/assets/logo/${
    environment === 'prod' ? 'prod' : 'stage'
  }/pl`;

  const logoFile = variant === 'dark' ? 'logo-dark.svg' : 'logo.svg';
  const mobileLogoFile = variant === 'dark' ? 'logo-dark-mobile.svg' : 'logo-mobile.svg';

  switch (sizes) {
    case 'small':
      return (
        <img
          src={`${baseUrl}/${mobileLogoFile}`}
          alt="logo"
          data-testid="MainLogo-mobile-logo"
          fetchPriority="high"
        />
      );
    case 'large':
      return (
        <img
          src={`${baseUrl}/${logoFile}`}
          alt="logo"
          data-testid="MainLogo-desktop-logo"
          fetchPriority="high"
        />
      );
    default:
      return (
        <picture>
          <source
            media="(max-width: 1025px)"
            srcSet={`${baseUrl}/${mobileLogoFile}`}
            data-testid="MainLogo-mobile-logo"
          />
          <img
            src={`${baseUrl}/${logoFile}`}
            alt="logo"
            data-testid="MainLogo-desktop-logo"
            fetchPriority="high"
          />
        </picture>
      );
  }
};

interface MainLogoProps {
  className?: string;
  variant?: 'default' | 'dark';
  sizes?: 'responsive' | 'small' | 'large';
}

/*
 * MainLogo component displays the brand logo.
 * The assets must be uploaded into the /web_assets/logo/{stage/prod}/{storeIdentifierName} directory in
 * Akamai NetStorage before we can enable the brand to use this component.
 *
 * Images should be validated in stage before being promoted to the prod folder!
 * Both prod & stage sub folders have their own unique CP code for clearing the Akamai cache independently.
 */

const MainLogo = ({ className = '', variant = 'default', sizes = 'responsive' }: MainLogoProps) => {
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const environmentConfig = useAppSelector(environmentConfigSelector);

  return (
    <div
      className={['main-logo', 'hmf-display-flex', 'hmf-justify-content-flex-start']
        .concat(className ? [className] : [])
        .join(' ')}
      data-testid="MainLogo">
      <a
        className="main-logo-link hmf-display-flex hmf-align-items-center hmf-justify-content-center"
        href={checkStores('/')}
        tabIndex={0}>
        {storeIdentifierName === 'pl' ? (
          <MainLogoImagePL
            environment={environmentConfig.environment}
            variant={variant}
            sizes={sizes}
          />
        ) : (
          <MainLogoImageDefault
            storeIdentifierName={storeIdentifierName}
            environment={environmentConfig.environment}
          />
        )}
      </a>
    </div>
  );
};

export default MainLogo;
